// Agregación del picking de un pedido de venta.
//
// El backend devuelve una fila por (carga, artículo). De ahí salen las dos
// vistas que necesita la pantalla: el avance de cada carga y el avance de cada
// artículo sumando todas sus cargas.
//
// El avance se mide sobre lo SOLICITADO en cargas, no sobre lo pedido en la
// línea de venta: responde «¿va bien la preparación de lo que se mandó
// preparar?». Una carga al 100% está lista, aunque falte lanzar más cantidad.
import type { CargaLinea, CargaEstadoPicking, FiltroEstadoCarga } from '@/types'

export interface ResumenPicking {
  solicitada: number
  /** Stock atado. Va aquí y no en `ResumenPesos` porque son unidades del mismo
   *  artículo que las otras tres, pero NO cierra con ellas: `solicitada` = picking
   *  + pendiente se cumple siempre, y la reservada es una cuarta cifra
   *  independiente que puede quedarse corta. El hueco entre las dos es el dato. */
  reservada: number
  picking: number
  pendiente: number
  pct: number
  estado: CargaEstadoPicking
}

/** Lo que falta por reservar de lo que se mandó preparar.
 *
 *  Se recorta a 0 porque se puede reservar de más y un negativo aquí se leería
 *  como si faltara: mismo criterio que `pendienteDeLanzar`. */
export function faltaPorReservar(solicitada: number, reservada: number): number {
  return Math.max(0, solicitada - reservada)
}

/** Bultos y peso de un conjunto de líneas de carga.
 *
 *  Va aparte de `ResumenPicking` porque responde a otra pregunta: el picking mide
 *  cómo va la preparación, y esto mide QUÉ se manda. Y sobre todo porque su hueco
 *  se comporta al revés — ver `sinPeso`. */
export interface ResumenPesos {
  /** Suma de las líneas que traen dato. `null` = ninguna lo traía. */
  cajas: number | null
  /** Kg netos. `null` = ninguna línea tenía peso. */
  neto: number | null
  /** Kg brutos SIN el peso del palet. `null` con el mismo criterio que `neto`. */
  bruto: number | null
  /** Cuántas líneas no tienen peso en el maestro. Con > 0 el total está
   *  incompleto y hay que decirlo: una suma parcial se lee como completa. */
  sinPeso: number
  /** Ídem para las cajas, que faltan por su cuenta (61 filas en origen). */
  sinCajas: number
  /** Alguna línea trae un bruto menor que su neto — `peso_caja_kg` mal informado
   *  en el maestro, 1.597 cargas afectadas. La cifra se pinta igual; esto sólo
   *  enciende el aviso. */
  sospechoso: boolean
}

/** Suma cajas y pesos sin convertir los huecos en ceros.
 *
 *  El `reduce` normal («a + (l.pesoNeto ?? 0)») haría justo lo contrario: una
 *  carga a la que le faltan tres artículos en el maestro daría un total con
 *  pinta de completo. Aquí el hueco se cuenta y sale a la pantalla. */
export function totalPesos(lineas: CargaLinea[]): ResumenPesos {
  let cajas: number | null = null
  let neto: number | null = null
  let bruto: number | null = null
  let sinPeso = 0
  let sinCajas = 0
  let sospechoso = false

  for (const l of lineas) {
    // `== null` a propósito: cubre null y undefined, y deja pasar el 0, que es
    // una medida real —hay 33 filas con neto exactamente 0 en origen—.
    if (l.pesoNeto == null && l.pesoBruto == null) sinPeso++
    if (l.cajas == null) sinCajas++; else cajas = (cajas ?? 0) + l.cajas
    if (l.pesoNeto != null) neto = (neto ?? 0) + l.pesoNeto
    if (l.pesoBruto != null) bruto = (bruto ?? 0) + l.pesoBruto
    if (l.pesoNeto != null && l.pesoBruto != null && l.pesoBruto < l.pesoNeto) {
      sospechoso = true
    }
  }

  return { cajas, neto, bruto, sinPeso, sinCajas, sospechoso }
}

export interface ResumenCarga extends ResumenPicking {
  carga: string
  /** Entidad legal (dataareaid) y pedido de la carga. Constantes dentro de ella;
   *  suben aquí porque la cabecera de la carga los necesita para las acciones
   *  (generación documental) y sacarlos de `lineas[0]` en la vista escondería que
   *  son propiedades de la carga, no de una línea suya. */
  empresa: string
  numPedido: string
  articulos: number
  /** Destino / cliente final. Constante dentro de la carga; puede faltar. */
  referenciaCliente?: string
  /** Segunda referencia de cliente de la carga. Constante dentro de ella. */
  referenciaCliente2?: string
  /** ¿Va paletizada? `null` = no consta y no se pinta. */
  paletizada?: boolean | null
  /** ¿Ya salió del almacén? Constante dentro de la carga. */
  expedida: boolean
  /** ¿Va por avión? Constante dentro de la carga. */
  aerea: boolean
  /** Bultos y peso de la carga, sumados de sus líneas. A diferencia de todo lo
   *  anterior NO es constante dentro de la carga: se calcula. */
  pesos: ResumenPesos
  /** Sus filas, para el desplegable. Ordenadas por artículo. */
  lineas: CargaLinea[]
}


/** Estado derivado de las cantidades. El origen no tiene columna de estado ni
 *  tabla de cabecera de cargas, así que esto es todo lo que los datos permiten. */
export function estadoDe(solicitada: number, picking: number): CargaEstadoPicking {
  if (picking <= 0) return 'pendiente'
  if (picking >= solicitada) return 'preparada'
  return 'preparando'
}

function resumir(
  solicitada: number, picking: number, pendiente: number, reservada: number,
): ResumenPicking {
  // solicitada = picking + pendiente se cumple siempre en origen (verificado
  // sobre 409.954 filas), pero el guard evita un NaN si algún día no. La
  // reservada queda FUERA de esa identidad a propósito: ver `ResumenPicking`.
  const pct = solicitada > 0 ? (picking / solicitada) * 100 : 0
  return { solicitada, reservada, picking, pendiente, pct, estado: estadoDe(solicitada, picking) }
}

const sumar = (ls: CargaLinea[]) => ls.reduce(
  (a, l) => {
    a.sol += l.qtySolicitada
    a.res += l.qtyReservada
    a.pick += l.qtyPicking
    a.pend += l.qtyPendiente
    return a
  },
  { sol: 0, res: 0, pick: 0, pend: 0 },
)

/** Avance global del pedido. */
export function totalPicking(lineas: CargaLinea[]): ResumenPicking {
  const { sol, res, pick, pend } = sumar(lineas)
  return resumir(sol, pick, pend, res)
}

export interface ResumenLinea extends ResumenPicking {
  transid: string
  /** Cargas en las que aparece esta línea. */
  cargas: string[]
}

/** Una entrada por transacción de inventario, sumando sus cargas.
 *
 *  La clave es ``transid``, que casa 1:1 con el ``inventtransid`` de la línea de
 *  venta. Alimenta la columna de avance en las bandas sin facturar: ahí lo que
 *  importa es cuánto ha preparado el almacén, no cuánto se ha servido — una
 *  línea puede estar picada al 73% y servida al 0% mientras no se expide. */
export function agruparPorTransid(lineas: CargaLinea[]): Map<string, ResumenLinea> {
  const map = new Map<string, CargaLinea[]>()
  for (const l of lineas) {
    if (!l.transid) continue
    const arr = map.get(l.transid)
    if (arr) arr.push(l); else map.set(l.transid, [l])
  }
  const out = new Map<string, ResumenLinea>()
  for (const [transid, ls] of map) {
    const { sol, res, pick, pend } = sumar(ls)
    out.set(transid, {
      transid,
      cargas: [...new Set(ls.map(l => l.carga))].sort(),
      ...resumir(sol, pick, pend, res),
    })
  }
  return out
}

/** Lo que de una fila de pedido aún no se ha lanzado a ninguna carga.
 *
 *  «Lanzado» es lo SOLICITADO en cargas (preparado + por preparar), sin filtrar
 *  por estado: mide lo que ya salió a almacén, no lo que el almacén ha
 *  terminado. Sin cargas, no se ha lanzado nada y queda pendiente todo.
 *
 *  Se recorta a 0 porque se puede lanzar de más, y el exceso se devuelve aparte
 *  para el tooltip: mismo criterio que `PickingBar`, que clampa al 100% y deja
 *  el crudo a la vista en vez de esconderlo. */
export function pendienteDeLanzar(qtyPedida: number, resumen?: ResumenLinea) {
  const lanzado = resumen?.solicitada ?? 0
  return {
    lanzado,
    pendiente: Math.max(0, qtyPedida - lanzado),
    exceso:    Math.max(0, lanzado - qtyPedida),
  }
}

/** Una entrada por carga, ordenada por número de carga. */
export function agruparPorCarga(lineas: CargaLinea[]): ResumenCarga[] {
  const map = new Map<string, CargaLinea[]>()
  for (const l of lineas) {
    const arr = map.get(l.carga)
    if (arr) arr.push(l); else map.set(l.carga, [l])
  }
  return [...map.entries()]
    .map(([carga, ls]) => {
      const { sol, res, pick, pend } = sumar(ls)
      return {
        carga,
        empresa: ls[0]?.empresa ?? '',
        numPedido: ls[0]?.numPedido ?? '',
        articulos: new Set(ls.map(l => l.articulo)).size,
        // Constante dentro de la carga, pero el campo va casi siempre vacío:
        // se coge el primer valor con contenido.
        referenciaCliente: ls.find(l => l.referenciaCliente?.trim())?.referenciaCliente,
        referenciaCliente2: ls.find(l => l.referenciaCliente2?.trim())?.referenciaCliente2,
        // Constante dentro de la carga. Se busca el primer valor que conste: aquí
        // `null` significa «no se sabe», no `false`, así que no vale coger el de
        // la primera fila a ciegas.
        paletizada: ls.find(l => l.paletizada != null)?.paletizada ?? null,
        // Constante dentro de la carga y sin valor «no se sabe»: basta la
        // primera fila. Si alguna viniera marcada, la carga ha salido.
        expedida: ls.some(l => l.expedida),
        // Igual de constante —comprobado en origen: 55.685 cargas, ninguna con
        // dos valores— y sin nulos, así que aquí no hay tercer estado.
        aerea: ls.some(l => l.aerea),
        pesos: totalPesos(ls),
        lineas: [...ls].sort((a, b) => a.articulo.localeCompare(b.articulo, 'es-ES')),
        ...resumir(sol, pick, pend, res),
      }
    })
    .sort((a, b) => a.carga.localeCompare(b.carga, 'es-ES'))
}


/** Las cargas que cuadran con el filtro de estado de almacén.
 *
 *  Sin filtro devuelve la lista tal cual — la misma referencia, para no forzar
 *  recálculos aguas abajo. El campo de origen no tiene valor «no se sabe»: una
 *  carga que no ha salido está en el almacén.
 */
export function filtrarPorEstado(
  cargas: CargaLinea[],
  filtro: FiltroEstadoCarga,
): CargaLinea[] {
  if (!filtro) return cargas
  return cargas.filter(c => (filtro === 'expedida' ? c.expedida : !c.expedida))
}
